import { Button, Modal, Tooltip, Upload, Input, Table, Space, Popconfirm, Divider, Empty, Spin } from 'antd'
import { useSelector } from 'react-redux'
import { EditOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { getLoginToken, requestMyActivities, store } from './store/store'
import { getRemoteUrl, handleRemoteAuthFailure } from './store/request'
import { useState, useRef, useEffect } from 'react';
import { reqChangeActivityName, reqDeleteActivity, setActivities, setActivityName, setSelectedActivity, asyncReqActivityTrace } from './store/activities'
import { useConversionStatus } from './useConversionStatus'

import Map from "./map/Map"
import { Layers, TileLayer, VectorLayer } from "./map/layers"
import { Controls, FullScreenControl, ZoomControl } from "./map/controls"
import { OsmCache } from "./map/sources/osm"
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import LineString from 'ol/geom/LineString'
import {Style,Stroke} from 'ol/style'
import * as olExtent from 'ol/extent'


function getActivityName(name) {
  if (name && name.length > 0)
    return name
  else
    return "Без имени"
}

function ActivitiesTable() {
  const {activities, activitiesLoading, selectedActivity} = useSelector(state => state.activities)

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});

  useEffect(() => {
    if (activities === undefined && activitiesLoading === false) {
      requestMyActivities()
    }
  }, [activities, activitiesLoading])

  // Use it to set focus on modal form apperance
  const textInput = useRef(null)

  const showChangeNameForm = (record) => {
    const data = {
      name: record.name,
      id: record.id
    }

    setModalData(data)
    setIsModalVisible(true)

    textInput.current.focus()
  }

  const handleDeleteActivity = (activityId) => {
    reqDeleteActivity(activityId)

    const newActivities = activities.filter((item) => item.id !== activityId);
    store.dispatch(setActivities({ activities: newActivities }))
  }

  const handleModalFormOk = () => {
    reqChangeActivityName(modalData.id, modalData.name)

    const payload = {...modalData}

    store.dispatch(setActivityName(payload))
    
    setIsModalVisible(false);
  }

  const handleModalFormCancel = () => {
    setIsModalVisible(false);
  }

  const handleModalInputChange = (e) => {
    const newData = {...modalData, name:e.target.value}
    setModalData(newData)
  }

  const handleRowClick = (record) => {
    store.dispatch(setSelectedActivity(record))
    store.dispatch(asyncReqActivityTrace(record.id))
  }

  const columns = [
    {
      width:20,
      key: 'selection',
      render: (_,record) => {
        if(selectedActivity && selectedActivity.id === record.id)
          return ('>')
        else
          return ('')
      }
    },
    {
      title: 'Название',
      dataIndex: 'name',
      ellipsis: true,
      render: (_,record) => {
        var name = getActivityName(record.name)
        return (<div>{name}</div>)
      }
    },
    {
      title: 'Дата',
      dataIndex: 'start_time',
      render: (_,record) => {
        const startTime = new Date(record.start_time * 1000)
        return (<div>{startTime.toLocaleString()}</div>)
      }
    },
    {
      title: 'Время в движении',
      dataIndex: 'timer_duration',
      render: (_, record) => {
        var date = new Date(null)
        date.setSeconds(record.timer_duration)
        const movingTime = date.toISOString().substring(11, 19)
        return (<div>{movingTime}</div>)
      }
    },
    {
      title: 'Дистанция',
      dataIndex: 'distance',
      render: (_, record) => {
        return (<div>{record.distance ? record.distance.toFixed(2) + " км" : ""}</div>)
      }
    },
    {
      title: 'Средний пульс',
      dataIndex: 'avg_hr',
      render: (_,record) => {
        return (<div>{record.avg_hr ? Math.round(record.avg_hr) : ""}</div>)
      }
    },
    {
      title: 'NP',
      dataIndex: 'np',
      render: (_,record) => {
        return (<div>{record.np ? Math.round(record.np): ""}</div>)
      }
    },
    {
      title: 'IF',
      dataIndex: 'if',
      render: (_,record) => {
        return (<div>{record.if ? record.if.toFixed(2) : ""}</div>)
      }
    },
    {
      title: 'TSS',
      dataIndex: 'tss',
      render: (_,record) => {
        return (<div>{record.tss ? Math.round(record.tss) : ""}</div>)
      }
    },
    {
      title: 'Действие',
      key: 'action',
      render: (_, record) => {
        return (<Space size="middle">
          <Popconfirm title="Точно удалить?" okText="Да" cancelText="Нет" onConfirm={() => handleDeleteActivity(record.id)}>
              <Button size="small" type="dashed" shape="circle" icon={<DeleteOutlined />} />
          </Popconfirm>
          <Tooltip title="Изменить название">
            <Button size="small" type="dashed" shape="circle" icon={<EditOutlined />} onClick={() => showChangeNameForm(record)} />
          </Tooltip>
        </Space>)
      }
    }
  ]

  return (<div>
    <Table
      size='small'
      loading={activitiesLoading}
      columns={columns}
      dataSource={activities}
      rowKey='id'
      pagination={{ pageSize: 3 }} 
      onRow = {
        (record) => {
          return {
            onClick:() => {handleRowClick(record)}
          }
        }
      }
    />

    <Modal title="Изменить название" visible={isModalVisible} onOk={handleModalFormOk} onCancel={handleModalFormCancel}>
      <Input ref={textInput} placeholder="Введите новое название" onChange={handleModalInputChange} value={modalData.name} />
    </Modal>
  </div>
    )
}

function ActivityDetails() {
  const {selectedActivity} = useSelector(state => state.activities)

  if (!selectedActivity) {
    return (<Empty description='Активность не выбрана'/>)
  }

  var date = new Date(null)
  date.setSeconds(selectedActivity.elapsed_duration)
  const totalTime = date.toISOString().substring(11, 19)
  
  var name = getActivityName(selectedActivity.name)

  return (
    <div>
      <Space direction='vertical'>
        <b>{name}</b>
        <Space split wrap size='small' direction='horizontal'>
          <span>Общее время: {totalTime}</span>
          {selectedActivity.distance && (<span>Дистанция: {selectedActivity.distance.toFixed(2) + " км"}</span>)}
          {selectedActivity.tss && (<span>TSS: {Math.round(selectedActivity.tss)}</span>)}
          {selectedActivity.avg_pwr && (<span>Средняя мощность: {Math.round(selectedActivity.avg_pwr)}</span>)}
          {selectedActivity.np && (<span>Нормализованная мощность: {Math.round(selectedActivity.np)}</span>)}
          {selectedActivity.avg_hr && (<span>Средний пульс: {Math.round(selectedActivity.avg_hr)}</span>)}
          <span>Средняя скорость: ###</span>
          <span>Спуск/подъём: ###/###</span>
        </Space>
      </Space>
      <ActivityMap />
    </div>
    )
}

function ActivityMap() {
  const {selectedActivityTrace, selectedActivity} = useSelector(state => state.activities)
  const [zoom] = useState(10);

  if (selectedActivity && selectedActivity.outdoor === 0) {
    return (<p>Тут будет график тренировки</p>)
  }

  if (!selectedActivityTrace || selectedActivityTrace.length === 0){
    return (<Spin tip="Грузим трек..."> <Map /> </Spin>)
  }

  let coords = []
  for (const p of selectedActivityTrace) {
    if (p['latitude'] && p['longitude'])
      coords.push([p['longitude'], p['latitude']])
  }

  var feature = new Feature({
    geometry: new LineString(coords)
  })

  const pathStyle = new Style({
    stroke: new Stroke({
      color: "blue",
      width: 2
    })
  })

  feature.getGeometry().transform('EPSG:4326', 'EPSG:3857')
  var vectorSource = new VectorSource({features: [feature]})

  const extent = feature.getGeometry().getExtent()
  const center = olExtent.getCenter(extent)

  return (
    <Map center={center} zoom={zoom}>
      <Layers>
        <TileLayer
          source={new OsmCache()}
          zIndex={0}
        />
        <VectorLayer
          source={vectorSource}
          style={pathStyle}
          zIndex={1}
        />
      </Layers>
      <Controls>
        <FullScreenControl />
        <ZoomControl />
      </Controls>
    </Map>
  )
}

function ViewActivities() {
  // Shared per-file conversion status (processing count, errors).
  const { files, errors, register } = useConversionStatus()

  // Single-file upload: antd keeps the list to show a per-file progress bar,
  // then clears it once the file reaches the server.
  const [singleFileList, setSingleFileList] = useState([])
  const [batchUploading, setBatchUploading] = useState(false)
  const [batchFileList, setBatchFileList] = useState([])
  const [uploadErrors, setUploadErrors] = useState([])

  // Synchronous guard for the batch upload. antd calls `onChange` once per
  // selected file (when `beforeUpload` returns false) with the SAME full
  // fileList; React batches the `batchUploading` state update, so that state
  // guard would let a second synchronous call slip through and re-POST the
  // same files (creating duplicate conversion rows). A ref is updated
  // synchronously and blocks the duplicate submission reliably.
  const batchInFlightRef = useRef(false)

  const singleUploadProps = {
    name: 'files',
    multiple: false,
    accept: '.fit',
    action: getRemoteUrl('activities/uploads'),
    headers: {
      'Authorization': `Bearer ${getLoginToken()}`
    },
    fileList: singleFileList,
    onChange(info) {
      let newFileList = [...info.fileList]
      let allDone = true

      newFileList = newFileList.map((f) => {
        if (f.status === 'done' && f.response && f.response.files) {
          register(f.response.files)
        } else if (f.status === 'error') {
          if (f.response && f.response.code === 'Unauthorized') {
            handleRemoteAuthFailure(getRemoteUrl('activities/uploads'), {
              headers: { 'Authorization': `Bearer ${getLoginToken()}` },
            })
          }
          const message =
            (f.response && f.response.detail) ||
            (f.error && f.error.message) ||
            'Не удалось загрузить файл'
          setUploadErrors(prev => [...prev, { filename: f.name, message }])
        }
        if (!f.status || f.status === 'uploading') allDone = false
        return f
      })

      if (allDone) newFileList = []
      setSingleFileList(newFileList)
    },
  }

  // Batch upload: one multipart POST for all selected files; no per-file
  // listing or progress bars — only a subtle in-flight state on the button.
  const batchUploadProps = {
    multiple: true,
    accept: '.fit',
    showUploadList: false,
    beforeUpload: () => false,
    fileList: batchFileList,
    onChange(info) {
      const selected = info.fileList
      if (!selected.length) return
      if (batchInFlightRef.current) return
      batchInFlightRef.current = true

      setBatchUploading(true)
      setBatchFileList([])

      const formData = new FormData()
      selected.forEach((f) => formData.append('files', f.originFileObj, f.name))

      fetch(getRemoteUrl('activities/uploads'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getLoginToken()}` },
        body: formData,
      })
        .then(async (response) => {
          if (!response.ok) {
            if (response.status === 401) {
              handleRemoteAuthFailure(getRemoteUrl('activities/uploads'), {
                headers: { 'Authorization': `Bearer ${getLoginToken()}` },
              })
            }
            let detail = `Статус ответа сервера ${response.status}`
            try {
              const body = await response.json()
              if (body && body.detail) detail = body.detail
            } catch (e) { /* ignore */ }
            throw new Error(detail)
          }
          return response.json()
        })
        .then((data) => {
          if (data && data.files) register(data.files)
        })
        .catch((e) => {
          setUploadErrors(prev => [
            ...prev,
            { filename: selected.map((f) => f.name).join(', '), message: e.message },
          ])
        })
        .finally(() => {
          batchInFlightRef.current = false
          setBatchUploading(false)
        })
    },
  }

  const pendingCount = files.length
  const MAX_VISIBLE = 20
  const visibleFiles = files.slice(0, MAX_VISIBLE)
  const hiddenCount = pendingCount - visibleFiles.length

  return (
    <div>
      <ActivitiesTable />

      {pendingCount > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Space size='small'>
            <Spin size='small' />
            <span>Обработка файлов: {pendingCount}</span>
          </Space>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {visibleFiles.map((file) => (
              <li key={file.id}>{file.filename} — обработка...</li>
            ))}
            {hiddenCount > 0 && <li>+{hiddenCount} более</li>}
          </ul>
        </div>
      )}

      {[...uploadErrors, ...errors].length > 0 && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
          {[...uploadErrors, ...errors].map((err, idx) => (
            <li key={idx} style={{ color: 'red' }}>
              {err.filename} - {err.message}
            </li>
          ))}
        </ul>
      )}

      <Space size='small' align='center'>
        <Upload {...singleUploadProps}>
          <Button icon={<UploadOutlined />}>Загрузить .fit файл</Button>
        </Upload>
        <Upload {...batchUploadProps}>
          <Button
            icon={<UploadOutlined />}
            disabled={batchUploading}
            loading={batchUploading}
          >
            {batchUploading ? 'Загрузка…' : 'Пакетная загрузка'}
          </Button>
        </Upload>
      </Space>

      <Divider dashed >Подробности</Divider>
      <ActivityDetails/>
    </div>
  )
}

export default ViewActivities;

